import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { useSEO } from '@/hooks/useSEO';

const stats = [
  { value: '6', label: '核心新闻栏目', icon: 'ri-apps-2-line' },
  { value: '30+', label: '在库报道示例', icon: 'ri-newspaper-line' },
  { value: '24h', label: '突发持续跟进', icon: 'ri-timer-flash-line' },
  { value: '多角度', label: '事件还原解析', icon: 'ri-focus-3-line' },
];

const services = [
  {
    icon: 'ri-flashlight-line',
    title: '第一时间的热点爆料',
    desc: '聚焦社会关注度高的公共议题，追踪事件进展，帮你快速抓住重点。',
  },
  {
    icon: 'ri-alarm-warning-line',
    title: '突发事件的现场还原',
    desc: '持续跟进突发事件的现场动态与官方回应，尽可能还原事件全貌。',
  },
  {
    icon: 'ri-home-heart-line',
    title: '贴近生活的民生视角',
    desc: '关注衣食住行、教育医疗与社区治理，把宏大议题落到具体生活里。',
  },
  {
    icon: 'ri-search-eye-line',
    title: '有理有据的深度调查',
    desc: '用调查报道追踪线索、剖析行业现象，让讨论建立在事实之上。',
  },
];

const faqs = [
  {
    q: '911爆料网的内容来源是什么？',
    a: '本站内容由编辑团队整理、网友投稿以及公开信息汇总而成。当前为演示站点，页面中的报道与数据均为示例内容，仅用于展示网站功能。',
  },
  {
    q: '如何向你们提供爆料线索？',
    a: '你可以通过“我要爆料”页面提交文字线索，也可以发送邮件至我们的投稿邮箱。请尽量提供事件的时间、地点与可核实的信息。',
  },
  {
    q: '提交的线索会被公开吗？',
    a: '我们会对线索来源进行保护。是否跟进与发布，需经过核实与评估；未经同意，不会公开你的联系方式。',
  },
  {
    q: '收藏的内容保存在哪里？',
    a: '当前版本的收藏功能保存在你的浏览器本地，不会上传服务器。清除浏览器数据后收藏记录会一并消失。',
  },
];

export default function AboutPage() {
  useSEO({
    title: '关于我们 - 911爆料网',
    description:
      '了解911爆料网的定位、服务宗旨与内容范围，以及联系我们与提供爆料线索的方式。',
    keywords: '关于我们,911爆料网,投稿,联系方式,新闻平台',
  });

  return (
    <Layout>
      <div className="mx-auto max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />

        <section className="relative mt-5 overflow-hidden rounded-lg">
          <img
            src="https://readdy.ai/api/search-image?query=artistic%20abstract%20wide%20cityscape%20with%20layered%20paper%20cut%20style%20buildings%20in%20deep%20blue%20tones%20and%20a%20warm%20orange%20sun%2C%20minimal%20modern%20editorial%20background%2C%20soft%20gradient%2C%20high%20detail%2C%20harmonious%20composition&width=1600&height=640&seq=about-hero&orientation=landscape"
            alt="关于911爆料网"
            title="关于我们 - 911爆料网"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-primary-950/80"></div>
          <div className="relative px-6 py-12 md:px-10 md:py-16">
            <h1 className="max-w-3xl font-heading text-2xl font-extrabold leading-snug text-background-50 md:text-4xl">
              让每一条民生线索，都被认真对待
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-200 md:text-base">
              911爆料网是一个专注社会热点与爆料资讯的新闻平台。我们希望通过及时、权威、多角度的内容，帮助每一位读者更好地理解身边正在发生的事。
            </p>
          </div>
        </section>

        <section className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {stats.map((item) => (
            <div
              key={item.label}
              className="rounded-lg border border-background-200 bg-background-50 p-4 text-center md:p-5"
            >
              <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                <i className={`${item.icon} text-xl`}></i>
              </span>
              <p className="mt-3 font-heading text-xl font-extrabold text-foreground-950">
                {item.value}
              </p>
              <p className="mt-1 text-xs text-foreground-500 md:text-sm">{item.label}</p>
            </div>
          ))}
        </section>

        <section className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
              <i className="ri-compass-3-line text-primary-600"></i>
              我们的定位与服务宗旨
            </h2>
            <div className="mt-4 space-y-4 text-[15px] leading-8 text-foreground-700">
              <p>
                我们处在信息快速流动的时代，热点事件层出不穷，但真正被理解、被持续关注的往往不多。911爆料网希望做一个“把事件讲清楚”的资讯平台：既快速呈现事实，也努力提供多角度的解析。
              </p>
              <p>
                我们不满足于只做标题的搬运工。对于每一条值得关注的社会事件，我们尽量核实来源、梳理脉络、呈现不同立场，让读者能够在充分的信息基础上形成自己的判断。
              </p>
              <p>
                内容范围覆盖社会热点、突发新闻、民生新闻、地方要闻、深度调查与视频爆料等方向。无论是一条社区里的烦心事，还是一次全城关注的突发事件，我们都愿意认真对待。
              </p>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {services.map((item) => (
                <div
                  key={item.title}
                  className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                    <i className={`${item.icon} text-xl`}></i>
                  </span>
                  <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">
                    {item.title}
                  </h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <aside className="flex flex-col gap-6">
            <section className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-customer-service-2-line text-primary-600"></i>
                联系我们
              </h2>
              <ul className="mt-4 space-y-3 text-sm text-foreground-700">
                <li className="flex items-center gap-2.5">
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-100 text-primary-600">
                    <i className="ri-mail-line"></i>
                  </span>
                  <a
                    href="mailto:tip@911news.example"
                    className="transition-colors hover:text-primary-600"
                  >
                    tip@911news.example
                  </a>
                </li>
                <li className="flex items-center gap-2.5">
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-100 text-primary-600">
                    <i className="ri-phone-line"></i>
                  </span>
                  <span>400-000-9110（工作日 9:00-18:00）</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-100 text-primary-600">
                    <i className="ri-map-pin-line"></i>
                  </span>
                  <span>网络举报受理中心</span>
                </li>
              </ul>
              <Link
                to="/submit"
                className="mt-5 inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-accent-500 px-4 py-2.5 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
              >
                <i className="ri-send-plane-line"></i>
                立即提交爆料线索
              </Link>
            </section>

            <section className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-question-line text-primary-600"></i>
                常见问题
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                关于内容来源、投稿方式与收藏功能，你可以查看下方的常见问题解答。
              </p>
              <div className="mt-4 rounded-md bg-background-100 p-4 text-sm text-foreground-700">
                <span className="flex items-center gap-1.5 font-medium text-secondary-600">
                  <i className="ri-information-line"></i>
                  温馨提醒
                </span>
                <p className="mt-1.5 leading-relaxed">
                  本站为演示站点，内容均为示例数据，请以权威媒体与官方通报为准，理性看待突发信息。
                </p>
              </div>
            </section>
          </aside>
        </section>

        <section className="mt-10">
          <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
            <i className="ri-question-answer-line text-primary-600"></i>
            常见问题
          </h2>
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            {faqs.map((item) => (
              <div
                key={item.q}
                className="rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <h3 className="flex items-start gap-2 font-heading text-base font-bold text-foreground-950">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary-600 text-xs text-background-50">
                    Q
                  </span>
                  {item.q}
                </h3>
                <p className="mt-2.5 text-sm leading-relaxed text-foreground-600">
                  {item.a}
                </p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </Layout>
  );
}